/**
 * Markdown do agente, desenhado como JSX.
 *
 * As respostas chegam em markdown — `## títulos`, `**negrito**`, tabelas em
 * canos. Mostradas cruas, a leitura vira decifração: uma tabela de faturamento
 * de doze meses fica uma parede de `|`.
 *
 * ## Por que não uma biblioteca
 *
 * Toda biblioteca de markdown produz **HTML**, e injetar HTML exige
 * `dangerouslySetInnerHTML` — que desliga o escape do React e é proibido por
 * teste neste projeto. O texto aqui vem de um modelo de linguagem, sobre dados
 * da companhia: exatamente o tipo de conteúdo em que não se confia.
 *
 * Este arquivo nunca produz HTML. Cada trecho vira elemento React, e o React
 * escapa o texto sozinho. Um `<script>` no meio da resposta aparece como
 * texto, que é o que se quer.
 *
 * ## O que ele entende, e o que ignora
 *
 * Títulos, negrito, código, listas, tabelas e parágrafos — o que o Copilot
 * Studio de fato devolve. Link, imagem e citação em bloco passam como texto:
 * acrescentar suporte a link significaria decidir para onde ele pode apontar,
 * e isso é uma conversa de segurança, não de formatação.
 */

/** `**negrito**` e `` `código` `` viram elementos. O resto é texto. */
function inline(text, key) {
  const parts = [];
  // Uma expressão só para os dois: alternar duas passadas deixaria
  // `**um `dois`**` com o resultado dependendo da ordem.
  const re = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let last = 0;
  let m;

  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const token = m[0];
    if (token.startsWith('**')) {
      parts.push(<strong key={`${key}-${m.index}`}>{token.slice(2, -2)}</strong>);
    } else {
      parts.push(<code key={`${key}-${m.index}`}>{token.slice(1, -1)}</code>);
    }
    last = m.index + token.length;
  }

  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

/** Linha de tabela em canos → células, sem os canos das bordas. */
const cells = (line) =>
  line
    .replace(/^\s*\|/, '')
    .replace(/\|\s*$/, '')
    .split('|')
    .map((c) => c.trim());

/** A linha `|---|---|` que separa cabeçalho de corpo. */
const isSeparator = (line) => /^\s*\|?[\s:|-]+\|[\s:|-]*$/.test(line) && line.includes('-');

const isTableRow = (line) => line.trim().startsWith('|') && line.includes('|', 1);
const isBullet = (line) => /^\s*[-–*]\s+/.test(line);
const bulletText = (line) => line.replace(/^\s*[-–*]\s+/, '');

export function Markdown({ text }) {
  if (typeof text !== 'string' || text.trim() === '') return null;

  const lines = text.split('\n');
  const blocks = [];
  let paragraph = [];

  const flushParagraph = () => {
    if (paragraph.length === 0) return;
    const key = `p-${blocks.length}`;
    blocks.push(
      <p key={key}>
        {paragraph.map((l, i) => (
          // O `<br>` preserva a quebra dentro do parágrafo. `key` pelo índice é
          // seguro: a lista é montada de uma vez e nunca reordenada.
          <span key={i}>
            {inline(l, `${key}-${i}`)}
            {i < paragraph.length - 1 && <br />}
          </span>
        ))}
      </p>,
    );
    paragraph = [];
  };

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];

    if (line.trim() === '') {
      flushParagraph();
      continue;
    }

    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading) {
      flushParagraph();
      const key = `h-${blocks.length}`;
      // Sempre `<h4>`, independente de quantos `#`. A hierarquia de títulos da
      // página é da página; a resposta do agente é conteúdo dentro dela, e um
      // `<h1>` aqui bagunçaria a leitura por leitor de tela.
      blocks.push(
        <h4 key={key} className="md-heading">
          {inline(heading[2], key)}
        </h4>,
      );
      continue;
    }

    if (isTableRow(line)) {
      flushParagraph();
      const rows = [];
      while (i < lines.length && isTableRow(lines[i])) {
        if (!isSeparator(lines[i])) rows.push(cells(lines[i]));
        i += 1;
      }
      i -= 1;

      const key = `t-${blocks.length}`;
      const [head, ...body] = rows;
      blocks.push(
        <div className="md-table-scroll" key={key}>
          <table className="md-table">
            <thead>
              <tr>
                {head.map((c, n) => (
                  <th key={n} scope="col">
                    {inline(c, `${key}-h${n}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {body.map((r, n) => (
                <tr key={n}>
                  {r.map((c, m) => (
                    <td key={m}>{inline(c, `${key}-${n}-${m}`)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }

    if (isBullet(line)) {
      flushParagraph();
      const items = [];
      while (i < lines.length && isBullet(lines[i])) {
        items.push(bulletText(lines[i]));
        i += 1;
      }
      i -= 1;

      const key = `u-${blocks.length}`;
      blocks.push(
        <ul className="md-list" key={key}>
          {items.map((it, n) => (
            <li key={n}>{inline(it, `${key}-${n}`)}</li>
          ))}
        </ul>,
      );
      continue;
    }

    paragraph.push(line);
  }

  flushParagraph();
  return <div className="md">{blocks}</div>;
}

export default Markdown;
